import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LogOut, User, CreditCard, Zap, Crown, Rocket, Building2, Home, ArrowLeft } from 'lucide-react';

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const getPlanBadge = () => {
    const plan = user?.subscriptionPlan || 'FREE';
    const planStyles = {
      FREE: { bg: 'bg-gray-100', text: 'text-gray-700', icon: Zap, label: 'Free' },
      PROFESSIONAL: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Rocket, label: 'Pro' },
      BUSINESS: { bg: 'bg-purple-100', text: 'text-purple-700', icon: Building2, label: 'Business' },
      ENTERPRISE: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Crown, label: 'Enterprise' },
    };
    const config = planStyles[plan] || planStyles.FREE;
    const Icon = config.icon;

    return (
      <div className={`flex items-center gap-1 px-2 py-1 ${config.bg} ${config.text} rounded-full text-xs font-medium`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </div>
    );
  };

  const showUpgradeButton = user?.subscriptionPlan === 'FREE' || !user?.subscriptionPlan;
  const isOnDashboard = location.pathname === '/dashboard';

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className="bg-white shadow-sm border-b">
      <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* Home Button */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
            title="Go to Home"
          >
            <Home className="w-5 h-5" />
          </button>

          {/* Back to Dashboard - show when not on dashboard */}
          {!isOnDashboard && (
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
              title="Back to Dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Dashboard</span>
            </button>
          )}

          <h1
            className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent cursor-pointer hover:opacity-80 transition"
            onClick={() => navigate('/dashboard')}
          >
            Resume Rankings
          </h1>
        </div>
        <div className="flex items-center gap-4">
          {/* Plan & Credits */}
          <div
            className="flex items-center gap-3 px-4 py-2 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100 transition"
            onClick={() => navigate('/pricing')}
            title="View pricing & subscription"
          >
            {getPlanBadge()}
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-blue-600">
                {user?.hasUnlimitedCredits ? 'Unlimited' : `${user?.credits || 0} Credits`}
              </span>
            </div>
          </div>

          {/* Upgrade Button - Show for Free users */}
          {showUpgradeButton && (
            <button
              onClick={() => navigate('/pricing')}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-medium rounded-lg transition shadow-sm"
            >
              <Zap className="w-4 h-4" />
              Upgrade
            </button>
          )}

          {/* User Info */}
          <div className="flex items-center gap-2 text-gray-600">
            <User className="w-5 h-5" />
            <span className="hidden sm:inline">{user?.fullName}</span>
          </div>

          {/* Logout */}
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition"
          >
            <LogOut className="w-5 h-5" />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;